import React from 'react';

import AddIcon from '@mui/icons-material/Add';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { useNavigate } from 'react-router-dom';

import { reverse } from 'named-urls';

import { Button } from 'components/Button';

import { ColumnDefinition } from 'components/Grid/types';

import Grid, { defaultRenderers } from 'components/Grid';

import { UserRole, RegisterEntityModule } from 'core/constants';
import { TaskStatus } from 'utils/api/modules/tasks/tasks.constants';
import { StyledTaskChip } from 'containers/Tasks/components/TasksTable/styles';
import { TaskStatusOperator } from 'components/Grid/operators/Status';

import AddActionModal from 'containers/FormsDetails/components/RelatedTab/AddActionModal';

import routes from 'core/routes';

import { EntityRelatedTabProps } from 'components/Entities/types';

import { useStore } from 'utils/hooks/store';

import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import AbstractWrapper from './AbstractWrapper';
import { defaultProps } from './types';

const TasksColumns = [
  {
    field: 'name',
    headerName: 'Task name',
    nativeFilter: true,
  },
  {
    field: 'assigned',
    headerName: 'Assigned to',
    renderCell: defaultRenderers.user,
    valueGetter: (value: unknown) => ({ value }),
    sortable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: ({ value }: { value: TaskStatus }) => <StyledTaskChip status={value} />,
    filterOperators: TaskStatusOperator(),
    nativeFilter: true,
  },
] as unknown as ColumnDefinition[];

const TasksRelatedToEntities: React.FunctionComponent<EntityRelatedTabProps> = ({ onError, store }) => {
  const { routing, session } = useStore();
  const { details, entityType } = store;

  const [openModal, setOpenModal] = React.useState(false);
  const refreshRef = React.useRef<(() => void) | null>(null);
  const onClose = (): void => {
    refreshRef.current?.();
    setOpenModal(false);
  };
  const navigate = useNavigate();
  const onRowClick: GridEventListener<'rowClick'> = (task: GridRowParams): void => {
    navigate(reverse(routes.dashboard.tasks.details, { taskId: task.row.uuid }));
  };

  const redirectToTasks = (): void => {
    routing.push(
      reverse(routes.dashboard[entityType as RegisterEntityModule].relatedActions, { entityId: details?.uuid || '' })
    );
  };

  return (
    <>
      <AbstractWrapper
        headerTitle="Tasks"
        headerAction={
          <>
            {ADMIN_AND_MANAGERS.includes(session.user?.role as UserRole) && !session.user?.readOnly && (
              <Button onClick={() => setOpenModal(true)} variant="text" size="medium" startIcon={<AddIcon />}>
                New Task
              </Button>
            )}
          </>
        }
        gridElement={
          <Grid
            onError={onError}
            onRowClick={onRowClick}
            onRefreshCallback={(callback): void => {
              refreshRef.current = callback;
            }}
            basicColumns={TasksColumns}
            dataURL={`/api/entities/${entityType}/${details?.uuid}/tasks/`}
            {...defaultProps}
          />
        }
        footerAction={
          <Button variant="text" size="medium" onClick={redirectToTasks}>
            View all...
          </Button>
        }
      />
      <AddActionModal
        openModal={openModal}
        onClose={onClose}
        extraParams={{ [entityType]: store.details ? store.details.uuid : '' }}
      />
    </>
  );
};

export default TasksRelatedToEntities;
